import { WorkReviewCount } from '@/components/visits/WorkReview';
import { VisitsPanel } from '@/components/visits/Visits';
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, FileSignature, Plus, Receipt, Users, Hammer } from "lucide-react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { displayDate, nzTodayIso } from "@/lib/dates";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const SCHEDULE = "/app/crew-planner?view=day";
const INVOICES = "/app/invoicing/customer-invoices";
const OFFICE = ["admin", "office", "super_admin"];
const STATUSES = { planned: "Planned", in_progress: "In progress", on_hold: "On hold", completed: "Completed" };

function Section({ title, children, action, className = "" }) {
  return <Card className={`min-w-0 p-4 sm:p-5 ${className}`}>
    <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
      <h2 className="text-base font-semibold">{title}</h2>{action}
    </div>{children}
  </Card>;
}
function TextLink({ to, children }) {
  return <Link to={to} className="inline-flex items-center gap-1 text-sm font-medium hover:underline underline-offset-4">{children}<ArrowRight aria-hidden="true" className="h-4 w-4 shrink-0" /></Link>;
}
function Metric({ label, value, hint, icon: Icon, to }) {
  return <Card className="min-w-0 p-4 sm:p-5">
    <Icon aria-hidden="true" className="mb-3 h-5 w-5 text-muted-foreground" />
    <p className="text-sm font-medium">{label}</p>
    <p className="mt-1 text-3xl font-semibold tabular-nums">{value.toLocaleString("en-NZ")}</p>
    <p className="mt-1 text-sm text-muted-foreground">{hint}</p>
    <div className="mt-3"><TextLink to={to}>View</TextLink></div>
  </Card>;
}
function JobLabel({ job }) {
  return <>
    <Link to={`/app/jobs/${encodeURIComponent(job.id)}`} className="font-semibold hover:underline break-words">
      {job.job_number && <span className="text-muted-foreground">{job.job_number} · </span>}{job.title || "Untitled job"}
    </Link>
    {job.client_name && <p className="mt-1 text-sm text-muted-foreground break-words">Customer · {job.client_name}</p>}
  </>;
}

export default function TradesOverview() {
  const { user } = useAuth();
  const [day, setDay] = useState(nzTodayIso);
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(false);
  const canCreateJob = OFFICE.includes(user.role);
  useEffect(() => {
    const timer = setInterval(() => setDay(nzTodayIso()), 60000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    setResult(null); setError(false);
    api.get("/dashboard/trades-summary", { signal: controller.signal }).then(({ data }) => {
      if (controller.signal.aborted) return;
      if (data.org_id !== user.org_id || data.for_date !== day) throw new Error("Dashboard context changed");
      setResult({ day, attempt, data });
    }).catch(() => { if (!controller.signal.aborted) setError(true); });
    return () => controller.abort();
  }, [user.org_id, user.id, user.role, day, attempt]);
  const data = result?.day === day && result?.attempt === attempt ? result.data : null;

  if (data?.empty_workspace) return <div className="p-4 sm:p-6 max-w-4xl space-y-5"><h1 className="text-3xl font-semibold">Your business starts here</h1><p className="text-muted-foreground">Add a customer and your first Job, or start with a quote.</p><Section title="Get started"><div className="flex flex-wrap gap-5">{canCreateJob && <><TextLink to="/app/clients">Add Customer</TextLink><TextLink to="/app/jobs/new">Create First Job</TextLink></>}<TextLink to="/app/quotes/new">Create Quote</TextLink></div></Section>{['admin','super_admin'].includes(user.role)&&<TextLink to="/app/onboarding">Resume business setup</TextLink>}</div>;

  return <div data-testid="trades-overview" className="min-h-screen bg-background px-4 py-5 sm:px-6 lg:px-7 lg:py-6 text-foreground">
    <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
      <div>
        <p className="mb-1 text-sm text-muted-foreground">{displayDate(day)}</p>
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Trades &amp; Services</h1>
        <p className="mt-1 text-sm text-muted-foreground">Your day, your team and the work ahead.</p>
      </div>
      {canCreateJob && <Button asChild><Link to="/app/jobs/new"><Plus aria-hidden="true" className="mr-2 h-4 w-4" />New Job</Link></Button>}
    </header>
    {error ? <Card className="p-5" role="alert">
      <h2 className="font-semibold">Dashboard unavailable</h2>
      <p className="my-2 text-sm text-muted-foreground">We couldn’t load your dashboard. Please try again.</p>
      <Button variant="outline" onClick={() => { setError(false); setAttempt(a => a + 1); }}>Retry</Button>
    </Card> : !data ? <div role="status" aria-label="Loading dashboard" aria-busy="true">
      <span className="sr-only">Loading dashboard…</span>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">{[0, 1, 2, 3].map(i => <Card key={i} className="h-44 animate-pulse bg-muted" />)}</div>
      <Card className="mt-5 h-64 animate-pulse bg-muted" />
    </div> : <>
      {data.can_plan && <div className="mb-5"><VisitsPanel start={day} title="Today’s visits" allowCreate /></div>}
      <section aria-label="Daily summary" className={`mb-5 grid grid-cols-2 gap-3 sm:gap-4 ${data.can_plan ? "xl:grid-cols-4" : ""}`}>
        {data.can_plan && <>
          <Metric label="Day-planned Jobs" value={data.schedule.jobs_today} hint="Scheduled today" icon={Hammer} to={SCHEDULE} />
          <Metric label="Day-planned Workers" value={data.schedule.workers_scheduled} hint="Assigned to jobs today" icon={Users} to={SCHEDULE} />
        </>}
        <Metric label="Quotes Awaiting Response" value={data.quotes_awaiting_response} hint="Sent or viewed · not expired" icon={FileSignature} to="/app/quotes" />
        <Metric label="Outstanding Invoices" value={data.outstanding_invoices} hint="Issued · balance remaining" icon={Receipt} to={INVOICES} />
      </section>
      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
        {data.can_plan && <Section title="Today’s day-planned work" action={<TextLink to={SCHEDULE}>View Schedule</TextLink>}>
            <p className="mb-3 text-sm text-muted-foreground">Day planning · scheduled assignments</p>
            {data.schedule.rows.length === 0 ? <p className="py-3 text-sm text-muted-foreground">No day allocations recorded for today.</p> : <ul className="divide-y divide-border">
              {data.schedule.rows.map(({ job, workers, worker_count }) => <li key={job.id} className="py-4 first:pt-0 last:pb-0">
                <JobLabel job={job} />
                {job.site_address && <p className="mt-1 text-sm text-muted-foreground break-words">{job.site_address}</p>}
                <div className="mt-3 flex flex-wrap gap-2">{workers.map((worker, index) => <span key={index} className="rounded-md bg-muted px-2 py-1 text-sm break-words">
                  {worker.name || "Worker"}{worker.start_time && /^([01]\d|2[0-3]):[0-5]\d$/.test(worker.start_time) ? ` · ${worker.start_time} start` : ""}
                </span>)}{worker_count > workers.length && <span className="py-1 text-sm text-muted-foreground">+{worker_count - workers.length} more</span>}</div>
                <p className="mt-2 text-xs font-medium text-muted-foreground">Scheduled</p>
              </li>)}
            </ul>}
            {data.schedule.jobs_today > data.schedule.rows.length && <p className="mt-4 text-sm text-muted-foreground">Showing {data.schedule.rows.length} of {data.schedule.jobs_today} scheduled jobs. View Schedule for all assignments.</p>}
          </Section>}
        <div className={data.can_plan ? "min-w-0 space-y-5" : "min-w-0 grid gap-5 sm:grid-cols-2 lg:col-span-2"}>
          <Section title="Needs Attention">
            {data.can_plan && <WorkReviewCount />}
            <ul className="space-y-3 text-sm">
              {data.pending_timesheets > 0 && <li><TextLink to="/app/time">{data.pending_timesheets} timesheets awaiting approval</TextLink></li>}
              {data.quotes_awaiting_response > 0 && <li><TextLink to="/app/quotes">{data.quotes_awaiting_response} quotes awaiting a customer response</TextLink></li>}
              {!(data.pending_timesheets > 0) && !data.quotes_awaiting_response && <li className="text-muted-foreground">No pending timesheets or quotes awaiting response.</li>}
            </ul>
          </Section>
          <Section title="Sales &amp; Customer Billing">
            <p className="mb-4 text-sm text-muted-foreground">Prepare a quote or review issued invoices and their payments.</p>
            <div className="flex flex-wrap gap-x-5 gap-y-3">
              <TextLink to="/app/quotes/new">New Quote</TextLink>
              <TextLink to={INVOICES}>View Invoices</TextLink>
            </div>
          </Section>
        </div>
        <Section title="Upcoming Job Starts" className="lg:col-span-2" action={data.can_plan && <TextLink to={SCHEDULE}>View Schedule</TextLink>}>
          <p className="mb-3 text-sm text-muted-foreground">Next 7 days · job start dates</p>
          {data.upcoming_jobs.length ? <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">{data.upcoming_jobs.map(job => <li key={job.id} className="min-w-0 border-l-2 border-border pl-3">
            <p className="mb-1 flex items-center gap-1 text-sm text-muted-foreground"><CalendarDays aria-hidden="true" className="h-4 w-4" />{displayDate(job.start_date)}</p><JobLabel job={job} />
          </li>)}</ul> : <p className="text-sm text-muted-foreground">No job starts recorded for the next 7 days.</p>}
          {data.upcoming_jobs.length === 5 && <p className="mt-3 text-sm text-muted-foreground">Showing the next 5 job starts. View all jobs for more.</p>}
        </Section>
        <Section title="Recent Jobs" className="lg:col-span-2" action={<TextLink to="/app/jobs">View all jobs</TextLink>}>
          <p className="mb-3 text-sm text-muted-foreground">Most recently created</p>
          {data.recent_jobs.length ? <ul className="divide-y divide-border">{data.recent_jobs.map(job => <li key={job.id} className="flex flex-wrap items-start justify-between gap-2 py-3 first:pt-0 last:pb-0">
            <div className="min-w-0 flex-1"><JobLabel job={job} /></div>
            <span className="rounded-md bg-muted px-2 py-1 text-xs font-medium">{STATUSES[job.status] || "Status unavailable"}</span>
          </li>)}</ul> : <p className="py-2 text-sm text-muted-foreground">No jobs yet.{canCreateJob ? " Create your first job to get started." : " Jobs will appear here when added."}</p>}
        </Section>
      </div>
    </>}
  </div>;
}
